import {
    useInsertionEffect,
    useLayoutEffect,
    type RefObject,
    useContext,
    useEffect,
    useRef
} from "react";
import {
    AccessibilityInfo,
    type HostInstance,
    BackHandler,
    Platform
} from "react-native";
import {
    context as portalContext,
    PortalKeyContext
} from "../portalize/context";

export type OverlayAccessibilityConstantType = {
    initialFocusRef?: RefObject<HostInstance | null>;
    returnFocusRef?: RefObject<HostInstance | null>;
    containerRef: RefObject<HostInstance | null>;
    isDisabledOverlay?: boolean;
    isAutoFocused?: boolean;
    onDismiss?: () => void;
    isActive?: boolean;
    isModal?: boolean;
};

export type OverlayContainerPropsType = {
    onAccessibilityEscape?: () => void;
    accessibilityViewIsModal?: boolean;
    "aria-modal"?: boolean;
    tabIndex?: -1;
};

export type OverlayAccessibilityType = {
    containerProps: OverlayContainerPropsType;
};

type OverlayStateType = {
    initialFocusRef?: RefObject<HostInstance | null>;
    returnFocusRef?: RefObject<HostInstance | null>;
    containerRef: RefObject<HostInstance | null>;
    isDisabledOverlay: boolean;
    onDismiss?: () => void;
    isModal: boolean;
};

type OverlaySubscriptionType = {
    remove: () => void;
};

const TABBABLE_SELECTOR = "a[href], area[href], button, input, select, textarea, iframe, object, embed, summary, audio[controls], video[controls], [tabindex], [contenteditable]";

const FOCUS_RETRY_LIMIT = 10;

const overlayStack: Array<RefObject<OverlayStateType>> = [];

let backSubscription: OverlaySubscriptionType | undefined;

const getWebDocument = () => {
    if(Platform.OS !== "web" || typeof globalThis.document === "undefined") {
        return undefined;
    }

    return globalThis.document;
};

const getHTMLElement = (node: unknown) => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

const getTabbableElements = (container: HTMLElement) => {
    return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((element) => {
        return element.tabIndex >= 0 && !element.matches(":disabled") && element.getClientRects().length > 0;
    });
};

const focusElement = (element: HTMLElement) => {
    element.focus({
        preventScroll: true
    });
};

const focusFirstElement = (container: HTMLElement) => {
    const [
        firstElement
    ] = getTabbableElements(container);

    focusElement(firstElement ?? container);
};

const focusNativeElement = (element?: HostInstance | null) => {
    if(!element) {
        return;
    }

    AccessibilityInfo.sendAccessibilityEvent(element, "focus");
};

const getTopOverlay = () => overlayStack[overlayStack.length - 1];

const getTopModalOverlayIndex = () => overlayStack.map((overlay) => overlay.current.isModal).lastIndexOf(true);

const isInsideOverlays = (target: Node, fromIndex: number) => {
    return overlayStack.slice(fromIndex).some((overlay) => {
        const container = getHTMLElement(overlay.current.containerRef.current);

        return container !== null && container.contains(target);
    });
};

const dismissOverlay = (overlay: OverlayStateType) => {
    if(overlay.isDisabledOverlay || !overlay.onDismiss) {
        return;
    }

    overlay.onDismiss();
};

const getIsDismissHandled = (isDisabledOverlay: boolean, onDismiss?: () => void) => isDisabledOverlay || onDismiss !== undefined;

const trapTabKey = (event: KeyboardEvent, overlayIndex: number) => {
    const container = getHTMLElement(overlayStack[overlayIndex]?.current.containerRef.current);

    if(!container) {
        return;
    }

    const activeElement = container.ownerDocument.activeElement;

    if(activeElement !== null && isInsideOverlays(activeElement, overlayIndex + 1)) {
        return;
    }

    const tabbableElements = getTabbableElements(container);

    const firstElement = tabbableElements[0];

    const lastElement = tabbableElements[tabbableElements.length - 1];

    if(!firstElement || !lastElement) {
        event.preventDefault();

        focusElement(container);

        return;
    }

    const isFocusInside = activeElement !== null && container.contains(activeElement);

    if(event.shiftKey) {
        if(!isFocusInside || activeElement === firstElement || activeElement === container) {
            event.preventDefault();

            focusElement(lastElement);
        }

        return;
    }

    if(!isFocusInside || activeElement === lastElement) {
        event.preventDefault();

        focusElement(firstElement);
    }
};

const onDocumentKeyDown = (event: KeyboardEvent) => {
    const topOverlay = getTopOverlay();

    if(!topOverlay || event.defaultPrevented || event.isComposing) {
        return;
    }

    if(event.key === "Escape") {
        event.preventDefault();

        dismissOverlay(topOverlay.current);

        return;
    }

    const modalIndex = getTopModalOverlayIndex();

    if(event.key === "Tab" && modalIndex !== -1) {
        trapTabKey(event, modalIndex);
    }
};

const onDocumentFocusIn = (event: FocusEvent) => {
    const modalIndex = getTopModalOverlayIndex();

    const container = getHTMLElement(overlayStack[modalIndex]?.current.containerRef.current);

    if(!container || !(event.target instanceof Node) || isInsideOverlays(event.target, modalIndex)) {
        return;
    }

    focusFirstElement(container);
};

const onHardwareBackPress = () => {
    const topOverlay = getTopOverlay();

    if(!topOverlay || !getIsDismissHandled(topOverlay.current.isDisabledOverlay, topOverlay.current.onDismiss)) {
        return false;
    }

    dismissOverlay(topOverlay.current);

    return true;
};

const addOverlay = (overlayRef: RefObject<OverlayStateType>) => {
    overlayStack.push(overlayRef);

    if(overlayStack.length > 1) {
        return;
    }

    const webDocument = getWebDocument();

    if(webDocument) {
        webDocument.addEventListener("keydown", onDocumentKeyDown);

        webDocument.addEventListener("focusin", onDocumentFocusIn);

        return;
    }

    if(Platform.OS !== "web") {
        backSubscription = BackHandler.addEventListener("hardwareBackPress", onHardwareBackPress);
    }
};

const removeOverlay = (overlayRef: RefObject<OverlayStateType>) => {
    const overlayIndex = overlayStack.indexOf(overlayRef);

    if(overlayIndex !== -1) {
        overlayStack.splice(overlayIndex, 1);
    }

    if(overlayStack.length) {
        return;
    }

    const webDocument = getWebDocument();

    if(webDocument) {
        webDocument.removeEventListener("keydown", onDocumentKeyDown);

        webDocument.removeEventListener("focusin", onDocumentFocusIn);
    }

    backSubscription?.remove();

    backSubscription = undefined;
};

const focusInitialElement = (overlay: OverlayStateType) => {
    const container = getHTMLElement(overlay.containerRef.current);

    if(!container) {
        return false;
    }

    if(container.contains(container.ownerDocument.activeElement)) {
        return true;
    }

    const initialElement = getHTMLElement(overlay.initialFocusRef?.current);

    if(initialElement) {
        focusElement(initialElement);

        return true;
    }

    focusFirstElement(container);

    return true;
};

const restoreFocus = (overlay: OverlayStateType, previousElement: HTMLElement | null) => {
    const targetElement = getHTMLElement(overlay.returnFocusRef?.current) ?? previousElement;

    if(!targetElement || !targetElement.isConnected) {
        return;
    }

    const container = getHTMLElement(overlay.containerRef.current);

    const activeElement = targetElement.ownerDocument.activeElement;

    const isFocusLost = !activeElement || activeElement === targetElement.ownerDocument.body;

    const isFocusInside = container !== null && activeElement !== null && container.contains(activeElement);

    if(!isFocusLost && !isFocusInside) {
        return;
    }

    if(!targetElement.closest("[inert]")) {
        focusElement(targetElement);

        return;
    }

    requestAnimationFrame(() => {
        const currentActiveElement = targetElement.ownerDocument.activeElement;

        const isCurrentFocusLost = !currentActiveElement || currentActiveElement === targetElement.ownerDocument.body;

        const isCurrentFocusInside = container !== null && currentActiveElement !== null && container.contains(currentActiveElement);

        if(!targetElement.isConnected || targetElement.closest("[inert]") || (!isCurrentFocusLost && !isCurrentFocusInside)) {
            return;
        }

        focusElement(targetElement);
    });
};

const useOverlayAccessibility = ({
    isDisabledOverlay = false,
    isAutoFocused = true,
    initialFocusRef,
    isActive = true,
    isModal = true,
    returnFocusRef,
    containerRef,
    onDismiss
}: OverlayAccessibilityConstantType): OverlayAccessibilityType => {
    const portalKey = useContext(PortalKeyContext);

    const overlayRef = useRef<OverlayStateType>({
        isDisabledOverlay,
        initialFocusRef,
        returnFocusRef,
        containerRef,
        onDismiss,
        isModal
    });

    const previousElementRef = useRef<HTMLElement | null>(null);

    useInsertionEffect(() => {
        if(isActive) {
            previousElementRef.current = getHTMLElement(getWebDocument()?.activeElement);
        }
    }, [
        isActive
    ]);

    useLayoutEffect(() => {
        overlayRef.current = {
            isDisabledOverlay,
            initialFocusRef,
            returnFocusRef,
            containerRef,
            onDismiss,
            isModal
        };
    });

    useEffect(() => {
        if(!isActive) {
            return;
        }

        const webDocument = getWebDocument();

        const previousElement = previousElementRef.current;

        let focusFrame: number | null = null;

        let focusAttempt = 0;

        const moveFocus = () => {
            focusFrame = null;

            if(!webDocument) {
                focusNativeElement(overlayRef.current.initialFocusRef?.current ?? overlayRef.current.containerRef.current);

                return;
            }

            if(!focusInitialElement(overlayRef.current) && focusAttempt < FOCUS_RETRY_LIMIT) {
                focusAttempt += 1;

                focusFrame = requestAnimationFrame(moveFocus);
            }
        };

        let hasFocusEntered = false;

        const handleFocusIn = (event: FocusEvent) => {
            const container = getHTMLElement(overlayRef.current.containerRef.current);

            if(container && event.target instanceof Node && container.contains(event.target)) {
                hasFocusEntered = true;
            }
        };

        addOverlay(overlayRef);

        if(isAutoFocused && webDocument) {
            moveFocus();
        } else if(isAutoFocused) {
            focusFrame = requestAnimationFrame(moveFocus);
        } else if(webDocument) {
            webDocument.addEventListener("focusin", handleFocusIn);
        }

        return () => {
            if(focusFrame !== null) {
                cancelAnimationFrame(focusFrame);
            }

            removeOverlay(overlayRef);

            if(!isAutoFocused && webDocument) {
                webDocument.removeEventListener("focusin", handleFocusIn);
            }

            if(!isAutoFocused && !hasFocusEntered) {
                return;
            }

            if(webDocument) {
                restoreFocus(overlayRef.current, previousElement);

                return;
            }

            focusNativeElement(overlayRef.current.returnFocusRef?.current);
        };
    }, [
        isActive
    ]);

    useEffect(() => {
        if(!isActive || !isModal || !portalKey) {
            return;
        }

        portalContext.setModal(portalKey, true);

        return () => {
            portalContext.setModal(portalKey, false);
        };
    }, [
        portalKey,
        isActive,
        isModal
    ]);

    const isModalActive = isActive && isModal;

    const isDismissHandled = getIsDismissHandled(isDisabledOverlay, onDismiss);

    const onAccessibilityEscape = () => {
        if(isActive) {
            dismissOverlay(overlayRef.current);
        }
    };

    return {
        containerProps: {
            onAccessibilityEscape: isDismissHandled ? onAccessibilityEscape : undefined,
            accessibilityViewIsModal: isModalActive ? true : undefined,
            tabIndex: Platform.OS === "web" ? -1 : undefined,
            "aria-modal": isModalActive ? true : undefined
        }
    };
};
export default useOverlayAccessibility;
